import { Location, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { BaseFormComponent } from 'components/form/base-form';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService, DEFAULT_NOTES_PAGE_SIZE } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { FILE_OBJECT_TYPE_LEGAL_ENTITY_NOTE } from 'types/file.interface';
import {
  CreateLegalEntityNoteRequest,
  LegalEntityNoteCard,
  LegalEntityNoteListItem,
  UpdateLegalEntityNoteRequest
} from 'types/legal-entity.interface';
import { formatDatetime } from 'util/date.utility';
import { MAX_LENGTH_DESCRIPTION, MIN_LENGTH_DESCRIPTION, getControlsWithErrors } from 'util/form.utility';

@Component({
  selector: 'app-legal-entity-notes-form',
  standalone: true,
  imports: [TranslateModule, NgbPagination, FileUploadComponent, TooltipComponent, ReactiveFormsModule, NgClass, ConfirmModalComponent],
  templateUrl: './legal-entity-notes-form.component.html',
  styleUrl: './legal-entity-notes-form.component.scss'
})
export class LegalEntityNotesFormComponent extends BaseFormComponent implements OnInit {
  @Input() legalEntityId!: string;
  @Input() notes?: LegalEntityNoteListItem[];
  @Input() isSubmittingNoteFiles: boolean = false;
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() notesChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly formatDatetime = formatDatetime;
  readonly legalEntityNoteFileType = FILE_OBJECT_TYPE_LEGAL_ENTITY_NOTE;
  readonly maxDescriptionLength: number = MAX_LENGTH_DESCRIPTION;

  userId: string = '';
  page: number = 1;
  pageSize: number = DEFAULT_NOTES_PAGE_SIZE;
  override fileUploadComponentCount = 1;

  userFullName: string = '';
  today: string = new Date().toISOString();
  editingNoteId: string | null = null;
  newNoteId: string | null = null;
  isAddingNote: boolean = false;
  isOpenConfirmDeleteNote: boolean = false;
  noteToDeleteId: string | null = null;
  isSaving: boolean = false;

  constructor(
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected override location: Location,
    protected override router: Router,
    protected authService: AuthorizationService,
    protected override toastService: ToastService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  get text(): AbstractControl<any, any> | null {
    return this.form.get('text');
  }

  get visibleNotes(): LegalEntityNoteListItem[] {
    if (!this.notes) return [];
    const start = (this.page - 1) * this.pageSize;
    return this.notes.slice(start, start + this.pageSize);
  }

  get isEditing(): boolean {
    return this.editingNoteId !== null || this.isAddingNote;
  }

  override ngOnInit(): void {
    this.userId = this.authService.getUserId();
    this.userFullName = this.authService.getUserFullName() || '';
  }

  onPageChange(): void {
    this.pageChange.emit(this.page);
  }

  onPageSizeChange(pageSize: Event) {
    const selectedValue = Number((pageSize.target as HTMLSelectElement).value);
    this.pageSize = selectedValue;
    this.page = 1;
  }

  override fillForm(): void {
    return;
  }

  startAdd(): void {
    this.editingNoteId = null;
    this.isAddingNote = true;
    this.initNoteForm();
    this.formChanged.emit(true);
  }

  startEdit(note: LegalEntityNoteListItem): void {
    this.isAddingNote = false;
    this.editingNoteId = note.id!;
    this.initNoteForm(note);
    this.formChanged.emit(true);
  }

  cancelEdit(): void {
    this.editingNoteId = null;
    this.isAddingNote = false;
    this.submitAttempted = false;
    this.form.reset();
    this.formChanged.emit(false);
  }

  saveNote(): void {
    if (this.form.valid) {
      this.submitAttempted = false;
      this.isSaving = true;

      if (this.editingNoteId) {
        const request: UpdateLegalEntityNoteRequest = {
          id: this.editingNoteId,
          text: this.text?.value
        };
        this.apiService.updateLegalEntityNote(request).subscribe(
          () => {
            this.toastService.showSuccess('updateLegalEntityNoteSuccess', '', { shouldTranslate: true });
            this.isSaving = false;
            this.isSubmittingNoteFiles = true;
          },
          (error: any) => {
            this.isSaving = false;
            this.toastService.showRequestError();
            console.error('Error updating legal entity note:', error);
          }
        );
      } else {
        const request: CreateLegalEntityNoteRequest = {
          legalEntityId: this.legalEntityId,
          text: this.text?.value
        };
        this.apiService.createLegalEntityNote(request).subscribe(
          (data: LegalEntityNoteCard) => {
            this.toastService.showSuccess('addLegalEntityNoteSuccess', '', { shouldTranslate: true });
            this.isSaving = false;
            this.newNoteId = data.id ?? null;
            this.isSubmittingNoteFiles = true;
          },
          (error: any) => {
            this.isSaving = false;
            this.toastService.showRequestError();
            console.error('Error creating legal entity note:', error);
          }
        );
      }
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.updateLegalEntityNoteItem');
    }
  }

  confirmDelete(noteId: string): void {
    this.noteToDeleteId = noteId;
    this.isOpenConfirmDeleteNote = true;
  }

  deleteNote(): void {
    if (!this.noteToDeleteId) return;
    this.apiService.deleteLegalEntityNote(this.noteToDeleteId).subscribe(
      () => {
        this.toastService.showSuccess('deleteLegalEntityNoteSuccess', '', { shouldTranslate: true });
        this.noteToDeleteId = null;
        this.isOpenConfirmDeleteNote = false;
        this.onNoteChanged();
      },
      (error: any) => {
        this.toastService.showRequestError();
        console.error('Error deleting legal entity note:', error);
      }
    );
  }

  onDeleteModalClose(): void {
    this.isOpenConfirmDeleteNote = false;
    this.noteToDeleteId = null;
  }

  override onSubmitSuccess(): void {
    this.isSubmittingNoteFiles = false;
    this.fileActions = [];
    this.uploadComponentsReceived = 0;
    this.editingNoteId = null;
    this.newNoteId = null;
    this.isAddingNote = false;
    this.submitAttempted = false;
    this.formChanged.emit(false);
    this.onNoteChanged();
  }

  private onNoteChanged(): void {
    this.pageChange.emit(this.page);
    this.notesChanged.emit();
  }

  private initNoteForm(note?: LegalEntityNoteListItem): void {
    this.form = this.formBuilder.group({
      text: [
        note?.text ?? '',
        [Validators.required, Validators.minLength(MIN_LENGTH_DESCRIPTION), Validators.maxLength(MAX_LENGTH_DESCRIPTION)]
      ]
    });
  }
}
